import React, { useEffect, useState } from "react";

function formatRemaining(ms) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m ${String(s).padStart(2, "0")}s`;
  if (m > 0) return `${m}m ${String(s).padStart(2, "0")}s`;
  return `${s}s`;
}

// Retro "be right back" overlay shown to non-owner visitors while the broadcaster
// has flipped the site into EPG-UPDATE mode. Owner sees a small banner instead
// so they can keep working.
export default function MaintenanceOverlay({ message, returnsAt }) {
  const [dots, setDots] = useState("");
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => {
      setDots((d) => (d.length >= 3 ? "" : d + "."));
    }, 450);
    return () => clearInterval(id);
  }, []);

  // Tick once a second for the countdown (only when we actually have a target).
  useEffect(() => {
    if (!returnsAt) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [returnsAt]);

  const text = (message || "Currently updating the EPG, we'll be right back...").replace(/\.+\s*$/, "");

  let countdown = null;
  if (returnsAt) {
    const target = new Date(returnsAt).getTime();
    if (Number.isFinite(target)) {
      const remaining = target - now;
      countdown = {
        remaining,
        targetTime: new Date(target).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
      };
    }
  }

  return (
    <div
      className="absolute inset-0 z-[55] bg-black text-[var(--phosphor)] flex flex-col items-center justify-center overflow-hidden"
      style={{ fontFamily: "JetBrains Mono, monospace" }}
      data-testid="maintenance-overlay"
    >
      {/* Scanlines + vignette match the rest of the CRT visual language */}
      <div
        className="absolute inset-0 pointer-events-none opacity-40"
        style={{
          background:
            "repeating-linear-gradient(0deg, color-mix(in srgb, var(--phosphor) 6%, transparent) 0 1px, transparent 1px 3px)",
        }}
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.9) 100%)",
        }}
      />

      <div className="relative text-center px-6 max-w-3xl">
        <div className="text-[10px] md:text-xs tracking-[0.5em] opacity-70 mb-4">
          ┌─ SCARTLEAD.TV BROADCAST CONTROL ─┐
        </div>
        <div className="text-3xl md:text-5xl lg:text-6xl tracking-[0.15em] chromatic mb-6 phosphor-glow">
          PLEASE STAND BY
        </div>
        <div
          className="text-base md:text-xl opacity-90 leading-snug min-h-[2.5em]"
          data-testid="maintenance-text"
        >
          {text}
          <span className="inline-block w-10 text-left">{dots}</span>
        </div>

        {/* Optional scheduled-return countdown */}
        {countdown && (
          <div
            className="mt-8 inline-flex flex-col items-center gap-1 px-6 py-3 border-2 border-[color-mix(in_srgb,var(--phosphor)_60%,transparent)] bg-black/40 phosphor-glow"
            data-testid="maintenance-countdown"
          >
            <div className="text-[10px] tracking-[0.4em] opacity-70">
              {countdown.remaining > 0 ? "BACK ON AIR IN" : "BROADCAST RESUMING…"}
            </div>
            <div className="text-3xl md:text-5xl tabular-nums chromatic">
              {countdown.remaining > 0 ? formatRemaining(countdown.remaining) : "00s"}
            </div>
            <div className="text-[10px] opacity-60 tracking-[0.3em]">
              ETA {countdown.targetTime}
            </div>
          </div>
        )}

        <div className="mt-10 text-[10px] md:text-xs tracking-[0.3em] opacity-60">
          BROADCAST WILL RESUME SHORTLY
        </div>
        <div className="mt-2 flex items-center justify-center gap-2 text-[10px] opacity-60">
          <span className="w-2 h-2 rounded-full bg-[#FFCC00] animate-pulse" />
          ENGINEERING ON SCENE
        </div>
      </div>

      <div className="absolute bottom-3 left-4 text-[10px] opacity-50 tracking-widest">
        CRT-1986
      </div>
      <div className="absolute bottom-3 right-4 text-[10px] opacity-50 tracking-widest">
        SCARTLEAD.TV
      </div>
    </div>
  );
}
